// Error messages are plain <p class="validation"> / <p class="feedback error"> placed near
// their field, so nothing ties them to it, nothing reliably announces them and a failed submit
// leaves focus on the button (3.3.1, 4.1.3). Rather than touch the ~130 places, wire them here.
//
// - Every message becomes an alert.
// - A message that belongs to a field describes it and marks it invalid. The field is the one
//   named by data-field="<id>" (shared blocks such as the card form, where every field's errors
//   sit together), else the only field inside the closest .inputWrapper / .promoCode.
// - After the user presses a button (or Enter in a field) the errors showing are surfaced:
//   focus moves to the first, and the rest are read out. That has to look at everything on
//   screen, not only what was just added -- a message that was already there (an expiry typed
//   wrong, one of two errors left unfixed) produces no new DOM and would never be announced.
// - A server error that arrives later (promo code, declined card) takes focus too, as long as
//   the user is still on the button they pressed.

let seq = 0
let pending = null // { el, at } for the last button press
let recent = new Set() // server errors that appeared after it
let announcer = null
const owners = new WeakMap() // message -> the field it describes

const MESSAGE = '.validation, .feedback.error'
const SURFACE_DELAY_MS = 300
const SERVER_ERROR_WINDOW_MS = 30000
// Buttons that change state rather than submit: pressing them must not pull focus to an error.
const NOT_A_SUBMIT = '.qtyWrapper *, .close, .close *, [aria-pressed], [aria-expanded], [aria-haspopup], [role="radio"], [role="tab"]'

const fieldFor = (message) => {
  if (message.dataset?.field) return document.getElementById(message.dataset.field)
  const fields = message
    .closest('.inputWrapper, .promoCode')
    ?.querySelectorAll('input:not([type="hidden"]):not(.iti__search-input), select, textarea')
  return fields?.length === 1 ? fields[0] : null
}

const describedBy = (field) => (field.getAttribute('aria-describedby') || '').split(' ').filter(Boolean)

export const attach = (message) => {
  message.setAttribute('role', 'alert')
  const field = fieldFor(message)
  if (!field) return null
  message.id = message.id || `field-error-${++seq}`
  field.setAttribute('aria-describedby', [...new Set([...describedBy(field), message.id])].join(' '))
  field.setAttribute('aria-invalid', 'true')
  owners.set(message, field)
  return field
}

export const detach = (message) => {
  const field = owners.get(message)
  if (!field) return
  owners.delete(message)
  const ids = describedBy(field).filter((id) => id !== message.id)
  if (ids.length) field.setAttribute('aria-describedby', ids.join(' '))
  else field.removeAttribute('aria-describedby')
  // Another message may still be describing the same field.
  if (!ids.some((id) => document.getElementById(id)?.matches(MESSAGE))) field.removeAttribute('aria-invalid')
}

const each = (node, fn) => {
  if (node.nodeType !== 1) return
  if (node.matches(MESSAGE)) fn(node)
  node.querySelectorAll(MESSAGE).forEach(fn)
}

const focusTarget = (message, field) => {
  const target = field || message
  if (target === message) message.setAttribute('tabindex', '-1')
  // focus() would jump there at once; scroll smoothly instead, as the rest of the app does
  target.focus({ preventScroll: true })
  const { top, bottom } = message.getBoundingClientRect()
  if (top < 0 || bottom > window.innerHeight) message.scrollIntoView({ behavior: 'smooth', block: 'center' })
}

// Still where the user left it: on the button they pressed, or on nothing (a loader took focus).
const userHasNotMovedOn = () => pending && [pending.el, document.body].includes(document.activeElement)

const announce = (text) => {
  if (!announcer) {
    announcer = document.createElement('div')
    announcer.setAttribute('aria-live', 'polite')
    announcer.setAttribute('aria-atomic', 'true')
    announcer.style.cssText = 'position:absolute;width:1px;height:1px;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap'
    document.body.appendChild(announcer)
  }
  announcer.textContent = ''
  setTimeout(() => (announcer.textContent = text), 60)
}

// After a button press: focus the first validation error showing, read the others out.
const surface = () => {
  if (!pending || !userHasNotMovedOn()) return
  const shown = [...document.querySelectorAll(MESSAGE)].filter(
    (m) => m.getClientRects().length && (m.classList.contains('validation') || recent.has(m))
  )
  if (!shown.length) return
  const [first, ...rest] = shown
  focusTarget(first, owners.get(first))
  if (rest.length) announce(rest.map((m) => m.textContent.trim()).join('. '))
}

export const watchFieldErrors = () => {
  const press = (el) => {
    pending = { el, at: Date.now() }
    recent = new Set()
    setTimeout(surface, SURFACE_DELAY_MS)
  }
  document.addEventListener(
    'click',
    (e) => {
      const el = e.target.closest?.('button, [role="button"]')
      if (el && !el.matches(NOT_A_SUBMIT)) press(el)
    },
    true
  )
  document.addEventListener('keydown', (e) => e.key === 'Enter' && e.target.matches?.('input') && press(e.target), true)

  new MutationObserver((records) => {
    const fresh = []
    records.forEach(({ addedNodes, removedNodes }) => {
      removedNodes.forEach((node) => each(node, detach))
      addedNodes.forEach((node) => each(node, (message) => fresh.push({ message, field: attach(message) })))
    })
    if (pending) fresh.forEach(({ message }) => message.classList.contains('feedback') && recent.add(message))
    // A server error that comes back after surface() has run.
    const late = pending && Date.now() - pending.at > SURFACE_DELAY_MS && Date.now() - pending.at < SERVER_ERROR_WINDOW_MS
    const error = fresh.find(({ message }) => message.classList.contains('feedback'))
    if (late && error && userHasNotMovedOn()) {
      focusTarget(error.message, null)
      pending = null
    }
  }).observe(document.body, { childList: true, subtree: true })
}
